import labAnalyses from './labAnalyses';
import LabOneDiagram from './LabOneDiagram';
import { GuidedDebugger, LabReference } from '../debugger/GuidedDebugger';

const DSLABS_SHELL = {
    title: (lab) => `DSLabs Visual Debugger — Lab ${lab.id}`,
    ref: (lab) => `Lab ${lab.id}`,
    panelLabel: 'Network',
    stateLabel: 'Protocol state',
    orientation: [
        ['01', 'Pick a case.', 'Each one isolates a single failure.'],
        ['02', 'Press Next.', 'One network event happens at a time.'],
        ['03', 'Watch the proof.', 'State and invariant sit on the right.'],
    ],
    renderDiagram: (lab, scenario, step) => lab.id === 1
        ? <LabOneDiagram currentStep={step} />
        : <LabFlowDiagram lab={lab} step={step} />,
};

export function LabSelector({ selectedLab, onSelect, labs = labAnalyses }) {
    return (
        <section className="lab-selector" aria-labelledby="lab-selector-title">
            <div className="lab-selector-heading">
                <p className="paxos-kicker"><span /> Explore the coursework</p>
                <h2 id="lab-selector-title">Choose a lab to inspect.</h2>
                <p>Each lab adds another layer of reliability, from retrying one request to moving transactional data across replica groups.</p>
            </div>
            <div className="lab-selector-tabs" role="tablist" aria-label="Distributed systems labs">
                {labs.map((lab) => (
                    <button
                        className={selectedLab === lab.id ? 'is-active' : ''}
                        key={lab.id}
                        type="button"
                        role="tab"
                        aria-selected={selectedLab === lab.id}
                        onClick={() => onSelect(lab.id)}
                    >
                        <span>Lab {lab.id}</span>
                        <strong>{lab.shortLabel}</strong>
                        <small>{lab.subtitle}</small>
                        <i>{selectedLab === lab.id ? 'Viewing' : 'Analyze'} →</i>
                    </button>
                ))}
            </div>
        </section>
    );
}

function LabFlowDiagram({ lab, step }) {
    const actorById = Object.fromEntries(lab.actors.map((actor) => [actor.id, actor]));
    const start = actorById[step.from];
    const end = actorById[step.to];
    const markerId = `lab-flow-arrow-${lab.id}`;
    const isLoop = step.from === step.to;
    const destination = step.dropped
        ? { x: start.x + ((end.x - start.x) * 0.68), y: start.y + ((end.y - start.y) * 0.68) }
        : end;

    return (
        <svg className="lab-flow-diagram" viewBox="0 0 900 380" role="img" aria-label={`Lab ${lab.id}: ${step.message}`}>
            <defs>
                <pattern id={`lab-grid-${lab.id}`} width="24" height="24" patternUnits="userSpaceOnUse">
                    <path d="M 24 0 L 0 0 0 24" fill="none" stroke="currentColor" strokeWidth="0.5" />
                </pattern>
                <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path className="lab-flow-arrow-head" d="M 0 0 L 10 5 L 0 10 z" />
                </marker>
            </defs>
            <rect className="lab-flow-grid" width="900" height="380" fill={`url(#lab-grid-${lab.id})`} />
            {lab.connections.map(([from, to]) => (
                <line
                    className="lab-flow-base"
                    key={`${from}-${to}`}
                    x1={actorById[from].x}
                    y1={actorById[from].y}
                    x2={actorById[to].x}
                    y2={actorById[to].y}
                />
            ))}

            <g className={`lab-flow-active${step.dropped ? ' is-dropped' : ''}`} key={`${lab.id}-${step.name}`} aria-hidden="true">
                {isLoop ? (
                    <circle className="lab-flow-loop" cx={start.x} cy={start.y} r="68">
                        <animate attributeName="r" values="60;90" dur="1.25s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.8;0" dur="1.25s" repeatCount="indefinite" />
                    </circle>
                ) : (
                    <>
                        <line
                            x1={start.x}
                            y1={start.y}
                            x2={destination.x}
                            y2={destination.y}
                            markerEnd={step.dropped ? undefined : `url(#${markerId})`}
                        />
                        <circle className="lab-flow-packet" cx={start.x} cy={start.y} r="8">
                            <animate attributeName="cx" from={start.x} to={destination.x} begin="0s" dur="1.25s" calcMode="linear" repeatCount="indefinite" />
                            <animate attributeName="cy" from={start.y} to={destination.y} begin="0s" dur="1.25s" calcMode="linear" repeatCount="indefinite" />
                            {step.dropped && <animate attributeName="opacity" values="1;1;0" keyTimes="0;0.7;1" begin="0s" dur="1.25s" repeatCount="indefinite" />}
                        </circle>
                        {step.dropped && (
                            <g className="lab-flow-drop-marker" transform={`translate(${destination.x} ${destination.y})`}>
                                <circle r="15" />
                                <path d="M -5 -5 L 5 5 M 5 -5 L -5 5" />
                            </g>
                        )}
                    </>
                )}
            </g>

            {lab.actors.map((actor, index) => {
                const sender = actor.id === step.from;
                const recipient = actor.id === step.to && !step.dropped;
                const droppedRecipient = actor.id === step.to && step.dropped;
                return (
                    <g
                        className={`lab-flow-node${sender ? ' is-sender' : ''}${recipient ? ' is-recipient' : ''}${droppedRecipient ? ' is-dropped-target' : ''}`}
                        key={actor.id}
                        transform={`translate(${actor.x - 82} ${actor.y - 47})`}
                    >
                        <rect width="164" height="94" rx="9" />
                        <text className="lab-flow-node-index" x="15" y="21">{String(index + 1).padStart(2, '0')}</text>
                        <text className="lab-flow-node-name" x="15" y="53">{actor.label}</text>
                        <text className="lab-flow-node-detail" x="15" y="73">{actor.detail}</text>
                    </g>
                );
            })}
        </svg>
    );
}

export function LabAnalysis({ lab, onTryInSandbox }) {
    return (
        <section className="lab-analysis" aria-labelledby={`lab-${lab.id}-title`}>
            <header className="lab-analysis-header">
                <div>
                    <p className="paxos-kicker"><span /> Lab {lab.id} analysis</p>
                    <h2 id={`lab-${lab.id}-title`}>{lab.title}</h2>
                </div>
                <div className="lab-question">
                    <span>The question</span>
                    <p>{lab.question}</p>
                    <span>The design</span>
                    <p>{lab.answer}</p>
                    <a className="lab-source-link" href={`mailto:adityapatel1204+wrk@gmail.com?subject=${encodeURIComponent(`Request: DSLabs Lab ${lab.id} notes and source code`)}`}>Request notes &amp; source code →</a>
                </div>
            </header>

            <div className={`lab-layer-map has-${lab.layers.length}-layers`}>
                {lab.layers.map(([name, description]) => (
                    <article key={name}><span>{name}</span><p>{description}</p></article>
                ))}
            </div>

            <GuidedDebugger lab={lab} shell={DSLABS_SHELL} onTryInSandbox={onTryInSandbox} />
            <LabReference lab={lab} />
        </section>
    );
}

export function LabThreeIntro({ lab }) {
    return (
        <section className="lab-three-intro" aria-labelledby="lab-3-title">
            <div>
                <p className="paxos-kicker"><span /> Lab 3 analysis</p>
                <h2 id="lab-3-title">{lab.title}</h2>
            </div>
            <div className="lab-three-question">
                <span>The question</span>
                <p>{lab.question}</p>
                <span>The design</span>
                <p>{lab.answer}</p>
                <a className="lab-source-link" href={`mailto:adityapatel1204+wrk@gmail.com?subject=${encodeURIComponent(`Request: DSLabs Lab ${lab.id} notes and source code`)}`}>Request notes &amp; source code →</a>
            </div>
            <div className="lab-three-guarantee">
                <span>Safety contract</span>
                <strong>{lab.guarantee}</strong>
            </div>
        </section>
    );
}
